import { useNavigate, useRoutes, useLocation } from 'react-router-dom';
import router from 'src/router';
import { CircularProgress } from '@mui/material';
import ThemeProvider from './theme/ThemeProvider';
import ToastProvider from './Context/ToastProvider';
import { useEffect, useRef, useState } from 'react';
import apiFetch from './Common/api/auth/Auth';
import { NotificationProvider } from './Context/NotificationContext';
import {
  SW_UPDATE_AVAILABLE_EVENT,
  applyServiceWorkerUpdate,
  getWorkerVersion,
} from './serviceWorkerRegistration';
import UpdateAvailableDialog from './components/UpdateAvailable';
import { getPostLoginHomePath } from 'src/Common/navigation/getPostLoginHomePath';
import {
  isPathAllowedDuringBillingRestriction,
  syncRestrictToBillingFromStoredToken,
} from 'src/Common/navigation/billingRestriction';

// Add public routes that don't require authentication
const PUBLIC_ROUTES = ['/login', '/register', '/register-store', '/forgot-password', '/reset-password', '/qr'];

function App() {
  const navigate = useNavigate();
  const location = useLocation();
  // Synchronously determine whether we need to wait for an API call before rendering.
  // If there is no stored token the user is clearly unauthenticated — show the login
  // page immediately instead of blocking behind a validate-token round-trip.
  const [isLoading, setIsLoading] = useState(() => {
    const token =
      sessionStorage.getItem('impersonateToken') || localStorage.getItem('authToken');
    const devLaunch = new URLSearchParams(window.location.search).get('devLaunch');
    return Boolean(token || devLaunch); // only show spinner when there is something to validate
  });
  const [authChecked, setAuthChecked] = useState(() => {
    const token =
      sessionStorage.getItem('impersonateToken') || localStorage.getItem('authToken');
    const devLaunch = new URLSearchParams(window.location.search).get('devLaunch');
    return !token && !devLaunch; // already "checked" — nothing to validate
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const authCheckDone = useRef(false);

  // ── New-version prompt ──────────────────────────────────────────────────
  // Replaces the corner toast this used to be: on a till screen a corner toast goes unnoticed
  // for days, so staff keep running an old build. Now a centred dialog (see components/
  // UpdateAvailable) — still dismissible, because forcing a reload on a cashier mid-sale is
  // worse than running one version behind for another few minutes.
  const [swRegistration, setSwRegistration] = useState<ServiceWorkerRegistration | null>(null);
  const [swVersion, setSwVersion] = useState<string | null>(null);
  const [updateOpen, setUpdateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const onSwUpdate = (e: Event) => {
      const ce = e as CustomEvent<{ registration: ServiceWorkerRegistration }>;
      const registration = ce.detail?.registration;
      if (!registration?.waiting) return;

      setSwRegistration(registration);
      setUpdateOpen(true);

      // Best-effort: name the version in the dialog. Never blocks the prompt.
      getWorkerVersion(registration.waiting).then((v) => {
        if (!cancelled) setSwVersion(v);
      });
    };

    window.addEventListener(SW_UPDATE_AVAILABLE_EVENT, onSwUpdate as EventListener);
    return () => {
      cancelled = true;
      window.removeEventListener(SW_UPDATE_AVAILABLE_EVENT, onSwUpdate as EventListener);
    };
  }, []);

  // Initial auth check - only runs once on mount
  useEffect(() => {
    if (authCheckDone.current) return;

    // devLaunch: popup opened by developer "Launch" button.
    // The popup calls developer-login itself using the developer's shared localStorage token,
    // then stores the result in sessionStorage (tab-specific) so the developer's session is untouched.
    const devLaunchParams = new URLSearchParams(window.location.search);
    const devLaunchId = devLaunchParams.get('devLaunch');
    const devLaunchUser = devLaunchParams.get('devUser');
    if (devLaunchId) {
      window.history.replaceState({}, '', window.location.pathname);
      authCheckDone.current = true;
      const setupStore = async () => {
        try {
          const data = await apiFetch('Authenticate/developer-login', {
            method: 'POST',
            body: JSON.stringify({
              companyId: parseInt(devLaunchId, 10),
              ...(devLaunchUser ? { targetUserId: devLaunchUser } : {}),
            }),
          });
          if (!data?.token) throw new Error('No token received');
          sessionStorage.setItem('impersonateToken', data.token);
          const roles = data?.Roles ?? data?.roles;
          sessionStorage.setItem('impersonateRoles',
            Array.isArray(roles) ? roles.join(',') : String(roles ?? ''));
          const rb = data?.RestrictToBilling ?? data?.restrictToBilling;
          sessionStorage.setItem('impersonateRestrictToBilling',
            (rb === true || rb === 'true') ? 'true' : 'false');
          const pg = data?.PermissionGroups ?? data?.permissionGroups;
          sessionStorage.setItem('impersonatePermissionGroups',
            (pg && Array.isArray(pg)) ? JSON.stringify(pg) : '');
          const st = data?.StoreType ?? data?.storeType;
          if (st) sessionStorage.setItem('impersonateStoreType', String(st));
          setIsAuthenticated(true);
          navigate(getPostLoginHomePath(), { replace: true });
        } catch {
          navigate('/login', { replace: true });
        } finally {
          setAuthChecked(true);
          setIsLoading(false);
        }
      };
      setupStore();
      return;
    }

    const token = sessionStorage.getItem('impersonateToken') || localStorage.getItem('authToken');
    const currentPath = location.pathname;

    // Always allow /qr route - it's public and should work for everyone
    if (currentPath === '/qr' || currentPath.startsWith('/qr?')) {
      setAuthChecked(true);
      setIsLoading(false);
      return;
    }

    authCheckDone.current = true;

    const checkAuth = async () => {
      if (token) {
        syncRestrictToBillingFromStoredToken();
        try {
          await apiFetch('Authenticate/validate-token', { method: 'GET' });
          setIsAuthenticated(true);
          
          // If authenticated and trying to access public route (except /qr), redirect to dashboard
          if (PUBLIC_ROUTES.includes(currentPath) && currentPath !== '/qr') {
            navigate(getPostLoginHomePath(), { replace: true });
          } else if (!PUBLIC_ROUTES.includes(currentPath) && !isPathAllowedDuringBillingRestriction(currentPath)) {
            navigate('/management/billing', { replace: true });
          }
        } catch (error) {
          console.log('Token validation failed:', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('employeeProfile');
          localStorage.removeItem('restrictToBilling');
          setIsAuthenticated(false);
          
          // Only redirect to login if not already on a public route
          if (!PUBLIC_ROUTES.includes(currentPath)) {
            navigate('/login', { replace: true });
          }
        }
      } else {
        setIsAuthenticated(false);
        // If no token and trying to access protected route, redirect to login
        // But allow /qr to be accessed without authentication
        if (!PUBLIC_ROUTES.includes(currentPath)) {
          navigate('/login', { replace: true });
        }
      }
      
      setAuthChecked(true);
      setIsLoading(false);
    };

    checkAuth();
  }, []); // Only run once on mount

  // Route protection - runs when pathname changes (but only after initial auth check)
  useEffect(() => {
    if (!authChecked) return; // Wait for initial auth check to complete

    const token = sessionStorage.getItem('impersonateToken') || localStorage.getItem('authToken');
    const currentPath = location.pathname;

    // Always allow /qr route
    if (currentPath === '/qr' || currentPath.startsWith('/qr?')) {
      return;
    }

    // Handle route protection without making API calls
    if (token && isAuthenticated) {
      // Has valid token and trying to access public route (except /qr), redirect to dashboard
      if (PUBLIC_ROUTES.includes(currentPath) && currentPath !== '/qr') {
        navigate(getPostLoginHomePath(), { replace: true });
      } else if (!PUBLIC_ROUTES.includes(currentPath) && !isPathAllowedDuringBillingRestriction(currentPath)) {
        navigate('/management/billing', { replace: true });
      }
    } else if (!token) {
      // No token and trying to access protected route
      if (!PUBLIC_ROUTES.includes(currentPath)) {
        navigate('/login', { replace: true });
      }
    }
  }, [location.pathname, authChecked, isAuthenticated, navigate]); // Only handle route protection, no API calls

  const content = useRoutes(router);

  const Loading = () => (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <CircularProgress />
    </div>
  );

  return (
    <ThemeProvider>
       <NotificationProvider>
        <ToastProvider>
          {isLoading || !authChecked ? <Loading /> : content}

          {/* Inside ThemeProvider so the dialog picks up the active light/dark theme. */}
          <UpdateAvailableDialog
            open={updateOpen && Boolean(swRegistration)}
            version={swVersion}
            onUpdate={() => {
              if (swRegistration) applyServiceWorkerUpdate(swRegistration);
            }}
            onDismiss={() => setUpdateOpen(false)}
          />
        </ToastProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;